import * as Sentry from '@sentry/react';
import { useState } from 'react';

import {
  Entertainer,
  EntertainerFeaturedCta,
  ProfileLink,
  useCurrentEntertainerQuery,
} from '@queries/EntertainerQueries';

import { useUpdateEntertainerMutation } from '@mutations/EntertainerMutations';

import Button from '@components/buttons/Button';
import { Popup } from '@components/layout/Popup/Popup';
import { PrimaryCtaCard } from '@components/layout/PrimaryCtaCard/PrimaryCtaCard';
import { TitleToolbar } from '@components/layout/TitleToolbar/TitleToolbar';

import { LinksListField } from '@screens/entertainer/components/LinksListField';

interface AddProfileSocialLinkPopupContentProps {
  onSubmit: (links: ProfileLink[]) => Promise<void>;
  onClose: () => void;
}

const AddProfileSocialLinkPopupContent = ({
  onSubmit,
  onClose,
}: AddProfileSocialLinkPopupContentProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [links, setLinks] = useState<ProfileLink[]>([{ type: 'custom', url: '', text: '' }]);

  const handleLinksChange = (updatedLinks: ProfileLink[]) => {
    setLinks(updatedLinks);
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);

      await onSubmit(links);

      onClose();
    } catch (error) {
      console.error(error);
      Sentry.captureException(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSubmit = links.every((link) => link.text && link.url);

  return (
    <div>
      <TitleToolbar text="Add social links" size="md" className="mb-4" />
      <div className="mb-1" style={{ fontSize: 13, opacity: 0.8 }}>
        Preview
      </div>
      <div className="mb-4 d-flex gap-2">
        {links.map((link) => (
          <div key={link.url}>
            <Button
              className="py-1 px-3"
              size="sm"
              variant="outlined"
              color="neutral"
              roundness="rounded"
              style={{ height: 30, opacity: link.text ? 1 : 0.5 }}
            >
              <span style={{ whiteSpace: 'nowrap' }}>{link.text || 'Edit link text'}</span>
            </Button>
          </div>
        ))}
      </div>
      <div className="d-grid gap-3">
        <LinksListField links={links} onChange={handleLinksChange} />
      </div>
      <div className="mt-4">
        <Button disabled={!canSubmit || isSubmitting} roundness="rounded" onClick={handleSubmit}>
          {links.length > 1 ? 'Save new links' : 'Save new link'}
        </Button>
      </div>
    </div>
  );
};

interface AddProfileSocialLinkPopupProps extends AddProfileSocialLinkPopupContentProps {
  isVisible: boolean;
}

export const AddProfileSocialLinkPopup = ({
  isVisible,
  ...sharedProps
}: AddProfileSocialLinkPopupProps) => {
  return (
    <Popup isVisible={isVisible} onClose={sharedProps.onClose}>
      {isVisible && (
        <Popup.Content>
          <AddProfileSocialLinkPopupContent {...sharedProps} />
        </Popup.Content>
      )}
    </Popup>
  );
};
